/**
 * Players: searchable + paginated roster with row actions (give shrooms, ban /
 * unban, confirm KYC) and an "Add player" form. Every destructive action goes
 * through confirmDialog first. The nickname (or "Card") opens the player card
 * (player-card.ts) — the one-player view with editing, hero, billing and the
 * deposit/withdraw blocks.
 */
import { el, clear, toast } from '../../dom';
import { adminApi, type AdminPlayer, type AdminPlayerPage, type AdminPlayerQuery } from '../../game-api';
import { fmtDate, fmtNum, pageInfo, pageLabel, validatePlayerForm, validateShroomGrant } from './format';
import {
  apiMessage, btn, checkbox, confirmDialog, emptyBox, errorBox, fieldError, input, labelled,
  modal, panel, pill, setFieldError, spinner, table, toastError,
} from './ui';
import { invoiceFor } from './invoices';
import { openPlayerCard } from './player-card';
import { ADMIN_SECTION_EVENT } from './section-event';

const PAGE_SIZE = 20;

export function renderPlayers(host: HTMLElement): () => void {
  let cancelled = false;
  const state: Required<Pick<AdminPlayerQuery, 'query' | 'status' | 'limit' | 'offset'>> = {
    query: '', status: 'all', limit: PAGE_SIZE, offset: 0,
  };

  const search = input('Search nickname or email…');
  const status = el('select',
    'px-2.5 py-1.5 rounded-lg bg-surface-2 border border-border text-sm text-text outline-none focus:border-gold',
    {},
    el('option', '', { value: 'all' }, 'All players'),
    el('option', '', { value: 'active' }, 'Active'),
    el('option', '', { value: 'banned' }, 'Banned'),
    el('option', '', { value: 'kyc' }, 'KYC verified'));

  const listHost = el('div', 'min-h-[80px]');
  const pager = el('div', 'flex items-center justify-between gap-2 mt-3 flex-wrap');

  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  search.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => { state.query = search.value.trim(); state.offset = 0; void load(); }, 300);
  });
  status.addEventListener('change', () => {
    state.status = (status as HTMLSelectElement).value as AdminPlayerQuery['status'] as 'all';
    state.offset = 0;
    void load();
  });

  async function load() {
    clear(listHost); clear(pager);
    listHost.appendChild(spinner());
    try {
      const page = await adminApi.players({ ...state });
      if (cancelled) return;
      renderPage(page);
    } catch (e) {
      if (cancelled) return;
      clear(listHost);
      listHost.appendChild(errorBox(apiMessage(e, "Couldn't load players."), () => void load()));
    }
  }

  function renderPage(page: AdminPlayerPage) {
    const items = page.items ?? [];
    clear(listHost); clear(pager);
    if (!items.length) {
      listHost.appendChild(emptyBox(state.query ? 'No players match that search.' : 'No players yet.'));
      return;
    }
    listHost.appendChild(table(
      ['Nickname', 'Email', 'Level', 'Shrooms', 'Joined', 'KYC', 'Status', 'Actions'],
      items.map((p) => [
        el('button', 'text-left text-gold underline-offset-2 hover:underline',
          { title: 'Open the player card', onClick: () => openCard(p) }, p.nickname ?? '—'),
        el('span', 'text-text-dim', {}, p.email),
        String(p.level ?? 0),
        fmtNum(p.shrooms),
        fmtDate(p.joinedAt),
        p.kycVerified ? pill('verified', 'ok') : pill('unverified', 'warn'),
        el('div', 'flex gap-1 flex-wrap', {},
          p.banned ? pill('banned', 'bad') : pill('active', 'ok'),
          p.createdByAdmin ? pill('admin-made', 'warn') : null,
          p.depositBlocked ? pill('no deposits', 'bad') : null,
          p.withdrawBlocked ? pill('no withdrawals', 'bad') : null),
        el('div', 'flex gap-1.5 flex-wrap', {},
          btn('Card', () => openCard(p), 'primary'),
          btn('Invoice', () => raiseInvoice(p)),
          btn('Shrooms', () => openGrant(p)),
          btn(p.banned ? 'Unban' : 'Ban', () => void toggleBan(p), p.banned ? 'default' : 'danger'),
          p.kycVerified ? el('span', 'hidden') : btn('Confirm KYC', () => void confirmKyc(p))),
      ])));

    const info = pageInfo(page.total ?? items.length, page.limit || PAGE_SIZE, page.offset ?? state.offset);
    pager.append(
      el('div', 'text-xs text-text-dim font-mono', {}, pageLabel(page.total ?? items.length, page.limit || PAGE_SIZE, page.offset ?? state.offset)),
      el('div', 'flex gap-2 items-center', {},
        btnDisabled('Prev', !info.hasPrev, () => { state.offset = Math.max(0, state.offset - state.limit); void load(); }),
        el('span', 'text-xs text-text-dim font-mono', {}, `Page ${info.page} / ${info.pageCount}`),
        btnDisabled('Next', !info.hasNext, () => { state.offset += state.limit; void load(); })));
  }

  /** Hand the player to the Invoices tab and switch to it, form open. */
  function raiseInvoice(p: AdminPlayer) {
    invoiceFor({ id: p.id, nickname: p.nickname ?? p.username ?? null, email: p.email });
    document.dispatchEvent(new CustomEvent(ADMIN_SECTION_EVENT, { detail: 'invoices' }));
  }

  function openCard(p: AdminPlayer) {
    openPlayerCard(p.id, p.nickname ?? p.email, {
      onChanged: () => void load(),
      onInvoice: (c) => raiseInvoice(c),
    });
  }

  // ── row actions ───────────────────────────────────────────────────────────
  function openGrant(p: AdminPlayer) {
    const amount = input('e.g. 500', '', 'number');
    const amountErr = fieldError();
    const reason = input('Reason (goes on the ledger)');
    const reasonErr = fieldError();
    const body = el('div', 'flex flex-col gap-3', {},
      el('div', 'text-xs text-text-dim', {}, `Granting shrooms to ${p.nickname ?? p.email}. Negative amounts remove shrooms.`),
      el('div', '', {}, labelled('Amount', amount), amountErr),
      el('div', '', {}, labelled('Reason', reason), reasonErr));
    const submit = btn('Give shrooms', async () => {
      const errors = validateShroomGrant(amount.value, reason.value);
      setFieldError(amountErr, errors.amount);
      setFieldError(reasonErr, errors.reason);
      if (Object.keys(errors).length) return;
      const n = Number(amount.value);
      const ok = await confirmDialog('Adjust shrooms',
        `${n > 0 ? 'Add' : 'Remove'} ${fmtNum(Math.abs(n))} shrooms ${n > 0 ? 'to' : 'from'} ${p.nickname ?? p.email}?`,
        'Yes, apply');
      if (!ok) return;
      try {
        await adminApi.giveShrooms(p.id, n, reason.value.trim());
        toast('Shrooms updated');
        close();
        void load();
      } catch (e) { toastError(e, 'Could not grant shrooms'); }
    }, 'primary');
    const close = modal('Give shrooms', body, [btn('Cancel', () => close()), submit]);
  }

  async function toggleBan(p: AdminPlayer) {
    const next = !p.banned;
    const ok = await confirmDialog(next ? 'Ban player' : 'Unban player',
      next ? `Ban ${p.nickname ?? p.email}? They will lose access immediately.`
        : `Restore access for ${p.nickname ?? p.email}?`,
      next ? 'Ban player' : 'Unban');
    if (!ok) return;
    try {
      await adminApi.setBanned(p.id, next);
      toast(next ? 'Player banned' : 'Player unbanned');
      void load();
    } catch (e) { toastError(e, 'Could not update the player'); }
  }

  async function confirmKyc(p: AdminPlayer) {
    const ok = await confirmDialog('Confirm KYC', `Mark ${p.nickname ?? p.email} as KYC verified?`, 'Confirm KYC');
    if (!ok) return;
    try {
      await adminApi.confirmKyc(p.id, true);
      toast('KYC confirmed');
      void load();
    } catch (e) { toastError(e, 'Could not confirm KYC'); }
  }

  // ── add player ────────────────────────────────────────────────────────────
  function openAdd() {
    const nickname = input('Nickname');
    const nickErr = fieldError();
    const email = input('player@example.com', '', 'email');
    const emailErr = fieldError();
    const password = input('At least 8 characters', '', 'password');
    const passErr = fieldError();
    const level = input('1', '1', 'number');
    const levelErr = fieldError();
    const shrooms = input('0', '0', 'number');
    const shroomsErr = fieldError();
    const kyc = checkbox('Mark KYC verified');

    const body = el('div', 'flex flex-col gap-3', {},
      el('div', '', {}, labelled('Nickname', nickname), nickErr),
      el('div', '', {}, labelled('Email', email), emailErr),
      el('div', '', {}, labelled('Password', password), passErr),
      el('div', 'grid grid-cols-2 gap-3', {},
        el('div', '', {}, labelled('Level', level), levelErr),
        el('div', '', {}, labelled('Shrooms', shrooms), shroomsErr)),
      kyc.wrap,
      el('div', 'text-[11px] text-text-dim', {},
        'A player created here is marked admin-created: invoices raised for them are never e-mailed.'));

    const submit = btn('Create player', async () => {
      const errors = validatePlayerForm({
        nickname: nickname.value, email: email.value, password: password.value,
        level: level.value, shrooms: shrooms.value,
      });
      setFieldError(nickErr, errors.nickname);
      setFieldError(emailErr, errors.email);
      setFieldError(passErr, errors.password);
      setFieldError(levelErr, errors.level);
      setFieldError(shroomsErr, errors.shrooms);
      if (Object.keys(errors).length) return;
      try {
        await adminApi.createPlayer({
          nickname: nickname.value.trim(),
          email: email.value.trim(),
          password: password.value,
          level: level.value === '' ? 1 : Number(level.value),
          shrooms: shrooms.value === '' ? 0 : Number(shrooms.value),
          kycVerified: kyc.input.checked,
        });
        toast('Player created');
        close();
        state.offset = 0;
        void load();
      } catch (e) { toastError(e, 'Could not create the player'); }
    }, 'primary');
    const close = modal('Add player', body, [btn('Cancel', () => close()), submit]);
  }

  const toolbar = el('div', 'flex flex-wrap items-center gap-2 mb-3', {},
    el('div', 'flex-1 min-w-[180px]', {}, search),
    status,
    btn('Add player', openAdd, 'primary'));

  host.appendChild(panel('Players', toolbar, listHost, pager));
  void load();
  return () => { cancelled = true; clearTimeout(searchTimer); };
}

function btnDisabled(label: string, disabled: boolean, onClick: () => void): HTMLButtonElement {
  const b = btn(label, () => { if (!disabled) onClick(); });
  if (disabled) { b.disabled = true; b.className += ' opacity-40 cursor-not-allowed'; }
  return b;
}
